/**
 * Performance Hero Card
 *
 * A prominent animated card showing the key performance summary.
 * Adapted from Portfolio's PerformanceSummaryCard for Trading Analysis.
 */

import { useEffect, useMemo } from "react";
import { formatCurrency, cn } from "@/lib/utils";
import {
  TrophyIcon,
  TrendingUpIcon,
  TargetIcon,
  ZapIcon,
  FlameIcon,
} from "lucide-react";
import type { AdvancedStats, CompletedTrade } from "@nordex/core";
import {
  type DateRangeValue,
  isCustomRange,
} from "@/components/ui/date-range-selector";
import { format } from "date-fns";

// Site's green and red colors
const SITE_GREEN = "#2ebd85";
const SITE_GREEN_LIGHT = "#5fd4a4";
const SITE_RED = "#f6465d";
const SITE_RED_LIGHT = "#ff8a9a";

// Inject keyframes for the animated glow effects
const injectPerformanceStyles = () => {
  const styleId = "trading-analysis-performance-glow";
  if (document.getElementById(styleId)) return;

  const style = document.createElement("style");
  style.id = styleId;
  style.textContent = `
    @keyframes ta-glow-spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
    @keyframes ta-glow-pulse {
      0%, 100% { opacity: 0.3; }
      50% { opacity: 0.6; }
    }
    @keyframes ta-number-glow {
      0%, 100% { text-shadow: 0 0 20px var(--glow-color); }
      50% { text-shadow: 0 0 40px var(--glow-color), 0 0 60px var(--glow-color); }
    }
  `;
  document.head.appendChild(style);
};

// Get readable label for date range
function getDateRangeLabel(dateRange: DateRangeValue): string {
  if (isCustomRange(dateRange)) {
    return `${format(dateRange.from, "MMM d")} - ${format(dateRange.to, "MMM d, yyyy")}`;
  }
  
  const labels: Record<string, string> = {
    today: "Today",
    yesterday: "Yesterday",
    this_week: "This Week",
    last_week: "Last Week",
    this_month: "This Month",
    last_month: "Last Month",
    last_7_days: "Last 7 Days",
    last_30_days: "Last 30 Days",
    last_90_days: "Last 90 Days",
    ytd: "Year to Date",
    all: "All Time",
  };
  return labels[dateRange] || dateRange;
}

interface PerformanceHeroCardProps {
  stats: AdvancedStats;
  trades: CompletedTrade[];
  dateRange: DateRangeValue;
  className?: string;
  /** When true, shows zero values and dims the glow effect */
  isEmpty?: boolean;
}

export function PerformanceHeroCard({
  stats,
  trades,
  dateRange,
  className,
  isEmpty = false,
}: PerformanceHeroCardProps) {
  // Inject styles on mount
  useEffect(() => {
    injectPerformanceStyles();
  }, []);

  // Calculate best day from trades
  const { bestDay, bestDayDate } = useMemo(() => {
    if (!trades.length) {
      return { bestDay: 0, bestDayDate: "" };
    }

    // Group trades by day and sum P&L
    const dailyPnl = new Map<string, number>();
    for (const trade of trades) {
      const day = trade.exitTime.toISOString().split("T")[0];
      const existing = dailyPnl.get(day) || 0;
      dailyPnl.set(day, existing + trade.netPnl);
    }

    let maxPnl = -Infinity;
    let maxDay = "";
    for (const [day, pnl] of dailyPnl) {
      if (pnl > maxPnl) {
        maxPnl = pnl;
        maxDay = day;
      }
    }

    return {
      bestDay: maxPnl === -Infinity ? 0 : maxPnl,
      bestDayDate: maxDay,
    };
  }, [trades]);

  const isPositive = stats.totalNetPnl >= 0;
  const glowColor = isPositive ? SITE_GREEN : SITE_RED;
  const glowColorLight = isPositive ? SITE_GREEN_LIGHT : SITE_RED_LIGHT;
  
  // Dim glow when empty
  const glowOpacity = isEmpty ? 0.2 : 0.4;

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div className={cn("group relative rounded-xl overflow-hidden", className)}>
      {/* Animated rotating gradient border */}
      <div className="absolute inset-0 rounded-xl overflow-hidden p-[2px]">
        <div
          className="absolute inset-[-200%]"
          style={{
            background: `conic-gradient(from 0deg, transparent 60%, ${glowColor}99 75%, ${glowColorLight}E6 85%, ${glowColor}99 95%, transparent 100%)`,
            animation: "ta-glow-spin 4s linear infinite",
            opacity: glowOpacity,
          }}
        />
      </div>

      {/* Pulsing border */}
      <div
        className="absolute inset-0 rounded-xl"
        style={{
          border: `2px solid ${glowColor}40`,
          animation: "ta-glow-pulse 2.5s ease-in-out infinite",
        }}
      />

      {/* Outer glow effect on hover */}
      <div
        className="absolute -inset-1 rounded-xl opacity-0 group-hover:opacity-100 blur-md transition-opacity duration-500"
        style={{
          background: `radial-gradient(circle, ${glowColor}30 0%, transparent 70%)`,
        }}
      />

      {/* Card content with glass effect */}
      <div className="relative bg-card/95 backdrop-blur-sm rounded-xl p-4 md:p-6 m-[2px]">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-base font-bold text-foreground">
              Performance Card
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {getDateRangeLabel(dateRange)}
            </p>
          </div>
        </div>

        {/* Main P&L Display */}
        <div className="text-center mb-6">
          <div className="text-[10px] text-muted-foreground mb-1.5 uppercase tracking-wider">
            Total Net P&L
          </div>
          <div
            className={cn(
              "text-4xl md:text-5xl font-bold tracking-tight",
              isEmpty ? "text-foreground" : (isPositive ? "text-bid-foreground" : "text-ask-foreground")
            )}
            style={{
              ["--glow-color" as string]: `${glowColor}60`,
              animation: isEmpty ? "none" : "ta-number-glow 3s ease-in-out infinite",
            }}
          >
            {isEmpty ? "$0.00" : (
              <>
                {isPositive ? "+" : "-"}$
                {formatCurrency(Math.abs(stats.totalNetPnl), 2)}
              </>
            )}
          </div>
          {isEmpty ? (
            <p className="text-xs text-muted-foreground mt-2">
              Make trades to see your performance
            </p>
          ) : (
            <div className="flex items-center justify-center gap-2 mt-2">
              <FlameIcon className="size-4 text-orange-500" />
              <span className="text-xs text-muted-foreground">
                Best Win Streak:{" "}
                <span className="font-semibold text-bid-foreground">
                  {stats.longestWinStreak} trades
                </span>
              </span>
            </div>
          )}
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {/* Win Rate */}
          <div className="bg-muted/30 rounded-lg p-3 text-center">
            <div className="flex items-center justify-center gap-1 text-[10px] text-muted-foreground mb-0.5">
              <TrophyIcon className="size-3 text-yellow-500" />
              Win Rate
            </div>
            <div
              className={cn(
                "text-xl font-bold",
                isEmpty ? "text-foreground" : (stats.winRate >= 50
                  ? "text-bid-foreground"
                  : "text-ask-foreground")
              )}
            >
              {isEmpty ? "0.0%" : `${stats.winRate.toFixed(1)}%`}
            </div>
          </div>

          {/* Profit Factor */}
          <div className="bg-muted/30 rounded-lg p-3 text-center">
            <div className="flex items-center justify-center gap-1 text-[10px] text-muted-foreground mb-0.5">
              <ZapIcon className="size-3 text-purple-500" />
              Profit Factor
            </div>
            <div
              className={cn(
                "text-xl font-bold",
                isEmpty ? "text-foreground" : (stats.profitFactor >= 1
                  ? "text-bid-foreground"
                  : "text-ask-foreground")
              )}
            >
              {isEmpty ? "0.00" : (stats.profitFactor === Infinity
                ? "∞"
                : stats.profitFactor.toFixed(2))}
            </div>
          </div>

          {/* Best Day */}
          <div className="bg-muted/30 rounded-lg p-3 text-center">
            <div className="flex items-center justify-center gap-1 text-[10px] text-muted-foreground mb-0.5">
              <TrendingUpIcon className="size-3 text-bid-foreground" />
              Best Day
            </div>
            <div
              className={cn(
                "text-xl font-bold",
                isEmpty ? "text-foreground" : (bestDay >= 0 ? "text-bid-foreground" : "text-ask-foreground")
              )}
            >
              ${isEmpty ? "0" : formatCurrency(Math.abs(bestDay), 0)}
            </div>
            <div className="text-[9px] text-muted-foreground">
              {isEmpty ? "—" : formatDate(bestDayDate)}
            </div>
          </div>

          {/* Total Trades */}
          <div className="bg-muted/30 rounded-lg p-3 text-center">
            <div className="flex items-center justify-center gap-1 text-[10px] text-muted-foreground mb-0.5">
              <TargetIcon className="size-3 text-blue-500" />
              Total Trades
            </div>
            <div className="text-xl font-bold text-foreground">
              {isEmpty ? "0" : stats.totalTrades}
            </div>
            <div className="text-[9px] text-muted-foreground">
              {isEmpty ? "0W / 0L" : `${stats.winningTrades}W / ${stats.losingTrades}L`}
            </div>
          </div>
        </div>

        {/* Additional Stats Row */}
        <div className="grid grid-cols-3 gap-3 mt-3 pt-3 border-t border-border/50">
          <div className="text-center">
            <div className="text-[10px] text-muted-foreground mb-0.5">
              Avg Win
            </div>
            <div className={cn("text-sm font-semibold", isEmpty ? "text-foreground" : "text-bid-foreground")}>
              ${isEmpty ? "0.00" : formatCurrency(stats.averageProfit, 2)}
            </div>
          </div>
          <div className="text-center">
            <div className="text-[10px] text-muted-foreground mb-0.5">
              Expectancy
            </div>
            <div className={cn("text-sm font-semibold", isEmpty ? "text-foreground" : (stats.expectancy >= 0 ? "text-bid-foreground" : "text-ask-foreground"))}>
              {isEmpty ? "$0.00" : (
                <>
                  {stats.expectancy >= 0 ? "+" : "-"}${formatCurrency(Math.abs(stats.expectancy), 2)}
                </>
              )}
            </div>
          </div>
          <div className="text-center">
            <div className="text-[10px] text-muted-foreground mb-0.5">
              Volume
            </div>
            <div className="text-sm font-semibold text-foreground">
              ${isEmpty ? "0" : formatCurrency(stats.totalVolume, 0)}
            </div>
          </div>
        </div>

        {/* Watermark for sharing */}
        <div className="absolute bottom-2 right-3 text-[8px] text-muted-foreground/30 font-medium tracking-wider">
          nordex.trade
        </div>
      </div>
    </div>
  );
}

